import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import {useState} from "react";
import { lazy, useEffect } from "react";

import RFxEvaluation from "./modules/vscDashboard/Pages/dashboardTabs/RFxEvaluation";

import { BrowserRouter, Routes, Route } from "react-router-dom";
import LandingPage from "./containers/LandingPage/LandingPage";
import ViewDocPage from "./components/ViewDocPage";
import {
  GENERAL_DEBOUNCE_TIME,
  PROTECTED,
  ROUTES,
  TIMEOUT_MILLISECONDS,
} from "./common/constants";
import { RoutesConfigType } from "./common/types";
import LoginPage from "./containers/LoginPage/LoginPage";
import AuthContainer from "./containers/AuthContainer/AuthContainer";
import Dashboard from "./modules/vscDashboard/Pages/landingPage/Dashboard";
import EmbeddedDocument from "./components/EmbeddedDocument/EmbeddedDocument";
import { ToastContainer } from "react-toastify";
import { useIdleTimer } from "react-idle-timer";
import { logout } from "./common/common";
import Profile from "./modules/vscDashboard/Pages/ProfilePage/Profile";
import AdminContainer from "./containers/AdminContainer/AdminContainer";
import AdminDashboard from "./modules/Admin/Pages/AdminDashboard/AdminDashboard";
import ClauseManagement from "./modules/Admin/Pages/PromptManagement/PromptManagement";
import PageNotFound from "./components/PageNotFound/PageNotFound";
import ManageRolePage from "./modules/Admin/Pages/ManageRole/ManageRolePage";
import { Suspense } from "react";
import Loader from "./components/Loader/Loader";
import DashboardTabs from "./modules/vscDashboard/Pages/dashboardTabs/DashboardTabs";
import { getProjectMappings } from "./services/common.service";
// import SourcingHubDashboard from "./modules/SourcingHubDashboard/SourcingHubDashboard";
// import SourcingHubChat from "./containers/WorkbenchDashboardContainer/SourcingHubChat/SourcingHubChat";
import RequestForm from "./components/workbenchDashboardRequestForm/RequestForm";

const ClauseReviewSummary = lazy(
  () =>
    import(
      "./modules/ManageContract/Pages/ClauseReviewSummaryPage/ClauseReviewSummary"
    )
);

const BulkClauseCheck = lazy(
  () =>
    import("./modules/ManageContract/Pages/BulkClauseCheckPage/BulkClauseCheck")
);

const ManageContract = lazy(
  () =>
    import("./modules/ManageContract/Pages/ManageContractPage/ManageContract")
);

const DemoLandingPage = lazy(
  () => import("./modules/Admin/Pages/DemoLandingPage/DemoLandingPage")
);

const ChabotContainer = lazy(
  () => import("./containers/ChabotContainer/ChatbotContainer")
);

const SourcingHubDashboard = lazy(
  () => import("./modules/SourcingHubDashboard/SourcingHubDashboard")
);

const SourcingHubChat = lazy(
  () => import("./containers/WorkbenchDashboardContainer/SourcingHubChat/SourcingHubChat")
);

const WorkbenchDashboardContainer = lazy(
  () => import("./containers/WorkbenchDashboardContainer/WorkbenchDasboardContainer")
);


function App() {
  const location = window.location;

  // 1) Track drawer open/closed
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 2) Provide a function to toggle
  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleOnUserIdle = () => {
    logout();
  };

  useIdleTimer({
    timeout: TIMEOUT_MILLISECONDS,
    onIdle: handleOnUserIdle,
    debounce: GENERAL_DEBOUNCE_TIME,
    disabled: location.pathname === ROUTES.login,
  });

  const RoutesConfig: RoutesConfigType = [
    { path: ROUTES.error, element: <PageNotFound /> },
    { path: "/contracts", element: <ViewDocPage />, type: PROTECTED },
    { path: ROUTES.login, element: <LoginPage />, showFooter: false },
    {
      path: ROUTES.dashboard,
      element: <Dashboard />,
      type: PROTECTED,
      showFooter: false,
    },
    {
      path: ROUTES.demoLandingPage,
      element: (
        <Suspense fallback={<Loader />}>
          <DemoLandingPage />,
        </Suspense>
      ),
      type: PROTECTED,
      showFooter: false,
      haveRightContainer: false,
    },
    {
      path: ROUTES.chatbot,
      element: (
        <Suspense fallback={<Loader />}>
          <ChabotContainer linkType="link1"/>,
        </Suspense>
      ),
      type: PROTECTED,
      showFooter: false,
      haveRightContainer: false,
    },
    {
      path: ROUTES.sourcingHubDashboard,
      element: (
        <Suspense fallback={<Loader />}>
          <SourcingHubDashboard />,
        </Suspense>
      ),
      type: PROTECTED,
      showFooter: false,
      haveRightContainer: false,
    },
    {
      path: ROUTES.doc,
      element: <EmbeddedDocument    onOpenDrawer={toggleDrawer(true)} />,
      type: PROTECTED,
      showFooter: false,
    },
    {
      path: ROUTES.clauseCheckDoc,
      element: <EmbeddedDocument    onOpenDrawer={toggleDrawer(true)} />,
      type: PROTECTED,
      showFooter: false,
    },
   
    {
      path: ROUTES.manageContracts,
      element: (
        <Suspense fallback={<Loader />}>
          <ManageContract />
        </Suspense>
      ),
      type: PROTECTED,
      showFooter: false,
    },
    // {
    //   path: ROUTES.bulkClauseCheck,
    //   element: (
    //     <Suspense fallback={<Loader />}>
    //       <BulkClauseCheck />
    //     </Suspense>
    //   ),
    //   type: PROTECTED,
    //   showFooter: false,
    // },
    {
      path: ROUTES.clauseSummary,
      element: (
        <Suspense fallback={<Loader />}>
          <ClauseReviewSummary />
        </Suspense>
      ),
  
      type: PROTECTED,
      showFooter: false,
    },
    // {
    //   path: ROUTES.profile,
    //   element: <Profile />,
    //   type: PROTECTED,
    //   showFooter: false,
    // },
    {
      path: ROUTES.adminDashboard,
      element: <AdminDashboard />,
      type: PROTECTED,
      allowedUsers: ["admin"],
    },
    {
      path: "/contracts",
      element: <ViewDocPage />,
      type: PROTECTED,
      allowedUsers: ["admin"],
    },
    {
      path: ROUTES.clauseManagement,
      element: <ClauseManagement />,
      type: PROTECTED,
      allowedUsers: ["admin"],
    },
    // {
    //   path: ROUTES.manageRole,
    //   element: <ManageRolePage />,
    //   type: PROTECTED,
    //   allowedUsers: ["admin"],
    // },
    {
      path: ROUTES.rfxEvaluation,
      element: <RFxEvaluation />,
      type: PROTECTED,
    },
    {
      path: ROUTES.dashboardTabs,
      element: <DashboardTabs />,
      type: PROTECTED,
    },
    {
      path: ROUTES.workbenchDashboard,
      element: <WorkbenchDashboardContainer />,
      type: PROTECTED,
      showFooter: false,
    },
    {
      path: ROUTES.sourcingHubChat,
      element: <SourcingHubChat />,
      type: PROTECTED,
      showFooter: false,
    },
    // workbenchDashboardRequestForm
    {
      path: ROUTES.workbenchDashboardRequestForm,
      element: <RequestForm/>,
      type: PROTECTED,
    },
  ];
  
  return (
    <BrowserRouter>
      <Routes>
        {RoutesConfig.map((route, routeIndex) => {
          const isAdminRoute = route?.allowedUsers?.includes("admin");
          if (route?.type === PROTECTED) {
            if (isAdminRoute) {
              return (
                <Route
                  key={routeIndex}
                  path={route.path}
                  element={
                    <AuthContainer>
                      <AdminContainer>{route.element}</AdminContainer>
                    </AuthContainer>
                  }
                />
              );
            }

            return (
              <Route
                key={routeIndex}
                path={route.path}
                element={
                  <AuthContainer>
                    <LandingPage
                      showFooter={route.showFooter}
                      haveRightContainer={route?.haveRightContainer}
                      drawerOpen={drawerOpen}
                      toggleDrawer={toggleDrawer}
                    >
                      {route.element}
                    </LandingPage>
                  </AuthContainer>
                }
              />
            );
          }
          return (
            <Route key={routeIndex} path={route.path} element={route.element} />
          );
        })}
      </Routes>

      <ToastContainer
        position="top-center"
        closeOnClick={true}
        pauseOnFocusLoss={false}
        newestOnTop={true}
        pauseOnHover={false}
      />
    </BrowserRouter>
  );
}

export default App;
